import { Delete } from '@mui/icons-material';
import
{
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import administrators from '../../api/administrators';
import { connect } from 'react-redux';
import { showError, showMessage } from '../../actions/uiActions';
import CSVFileValidator from 'csv-file-validator';
import BulkArchive from '../../components/BulkArchive';
import AdministratorApi from '../../api/administrators';
import BulkReassign from '../../components/BulkReassign';
import ConfirmAction from '../../components/ConfirmAction';

const BulkActions = ({ showError, showMessage }) =>
{
  const [accountManagers, setAccountManagers] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [archiveActions, setArchiveActions] = useState({
    reason_key: '',
    reason_description: '',
  });
  const [emails, setEmails] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [selectedAccountManager, setSelectedAccountManager] = useState(null);
  const [unAssignedUsers, setUnassignedUsers] = useState([]);
  const [chatbotEnabled, setChatbotEnabled] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loadingChatbotConfig, setLoadingChatbotConfig] = useState(false);

  const [file, setFile] = useState();

  useEffect(() =>
  {
    getAccountManagers();
  }, []);

  const getAccountManagers = async () =>
  {
    const accountManagers = await AdministratorApi.getList();
    setAccountManagers(accountManagers);
  };

  const fetchChatbotConfiguration = async () =>
  {
    try
    {
      setLoadingChatbotConfig(true);
      const config = await AdministratorApi.getChatbotConfiguration();
      setChatbotEnabled(config.enabled);
    } catch (error)
    {
      showError(error.message);
    } finally
    {
      setLoadingChatbotConfig(false);
    }
  };

  const handleFileChange = async ({ target }) =>
  {
    const emails = await CSVFileValidator(target.files[0], {
      skipEmptyLines: true,
      headers: [{ name: 'Email address', inputName: 'email', required: true }],
    });
    if (emails.data.length > 500)
    {
      showError('We can only process a maximum of 500 borrowers every minute');
    } else
    {
      const mappedEmails = emails.data.map((each) => each.email);
      setEmails(mappedEmails);
      setFile(target.files[0]);
    }
  };

  const handleDeleteFile = () =>
  {
    setFile(undefined);
    setEmails([]);
  };

  const disabled = useMemo(() =>
  {
    if (!bulkAction)
    {
      return true;
    }
    if (
      bulkAction === 'archive' &&
      (!archiveActions.reason_description || !archiveActions.reason_key)
    )
    {
      return true;
    }
    if (bulkAction === 'reassign' && !selectedAccountManager)
    {
      return true;
    }
    if (bulkAction === 'toggle_chatbot')
    {
      return false;
    }
    if (!file) return true;
    return false;
  }, [bulkAction, archiveActions, file, selectedAccountManager]);

  const renderUploadFile = () => (
    <div className='mt-4'>
      <input
        accept='.csv'
        type='file'
        id='file-input'
        className='file-input'
        onChange={handleFileChange}
      />
      <label htmlFor='file-input' className='file-upload'>
        Upload file
      </label>
    </div>
  );
  const renderPreviewFile = () => (
    <div
      className='file-upload'
      style={{ display: 'flex', alignItems: 'center' }}
    >
      <p style={{ margin: 0 }}>{file.name}</p>
      <IconButton onClick={handleDeleteFile}>
        <Delete />
      </IconButton>
    </div>
  );

  const handleSubmit = async (e) =>
  {
    e.preventDefault();
    if (bulkAction === 'toggle_chatbot')
    {
      setShowConfirmModal(true);
      return;
    }
    await processBulkAction();
  };

  const processBulkAction = async () =>
  {
    try
    {
      setProcessing(true);
      if (bulkAction === 'archive')
      {
        const payload = {
          emails,
          details: archiveActions,
        };
        await administrators.bulkArchiveUsers(payload);
        showMessage('Bulk action has been completed successfully');
      } else if (bulkAction === 'reassign')
      {
        const payload = {
          emails,
          accountManagerId: selectedAccountManager,
        };
        const data = await administrators.bulkAssignUsers(payload);
        setUnassignedUsers(data.unAssignedUsers);
        showMessage('Bulk action has been completed successfully');
      } else if (bulkAction === 'toggle_chatbot')
      {
        await administrators.toggleChatbotGlobally(chatbotEnabled);
        showMessage(`Chatbot has been ${chatbotEnabled ? 'enabled' : 'disabled'} globally`);
      }
      resetValue();
      setProcessing(false);
    } catch (error)
    {
      showError(error.message);
      setProcessing(false);
    }
  };

  const handleConfirmChatbotToggle = async () =>
  {
    setShowConfirmModal(false);
    await processBulkAction();
  };

  const handleCancelConfirm = () =>
  {
    setShowConfirmModal(false);
  };

  const resetValue = () =>
  {
    setBulkAction('');
    setArchiveActions({
      reason_key: '',
      reason_description: '',
    });
    setEmails([]);
    setFile(undefined);
    setChatbotEnabled(false);
  };

  return (
    <div className='animated fadeIn'>
      <div
        className='fax-m-12'
        style={{
          width: '50%',
        }}
      >
        <form onSubmit={handleSubmit}>
          <FormControl className='full-width font-normal'>
            <InputLabel className='font-normal full-width' htmlFor='status'>
              Select bulk action
            </InputLabel>
            <Select
              className='font-normal full-width'
              inputProps={{
                name: 'status',
                id: 'status',
              }}
              value={bulkAction}
              onChange={(e) =>
              {
                setBulkAction(e.target.value);
                if (e.target.value === 'toggle_chatbot')
                {
                  fetchChatbotConfiguration();
                }
              }}
            >
              <MenuItem value='archive'>Bulk Archive</MenuItem>
              <MenuItem value='reassign'>Bulk Reassign</MenuItem>
              <MenuItem value='toggle_chatbot'>Toggle Chatbot Globally</MenuItem>
            </Select>
          </FormControl>
          {bulkAction === 'archive' && (
            <BulkArchive
              archiveActions={archiveActions}
              setArchiveActions={setArchiveActions}
            />
          )}
          {bulkAction === 'reassign' && (
            <BulkReassign
              accountManagers={accountManagers}
              accountManager={selectedAccountManager}
              setAccountManager={setSelectedAccountManager}
            />
          )}
          {bulkAction === 'toggle_chatbot' && (
            <div className='mt-4'>
              {loadingChatbotConfig ? (
                <LinearProgress />
              ) : (
                <FormControlLabel
                  control={
                    <Switch
                      checked={chatbotEnabled}
                      onChange={(e) => setChatbotEnabled(e.target.checked)}
                      color='primary'
                    />
                  }
                  label={chatbotEnabled ? 'Enable Chatbot' : 'Disable Chatbot'}
                />
              )}
            </div>
          )}
          {bulkAction !== 'toggle_chatbot' && (file ? renderPreviewFile() : renderUploadFile())}
          {!processing && (
            <Button
              variant='outlined'
              color='primary'
              autoFocus
              className='mt-4'
              style={{ width: '100%' }}
              disabled={disabled}
              type='submit'
            >
              Submit
            </Button>
          )}
          {unAssignedUsers.length > 0 && (
            <div className='mt-4'>
              <h5>Unassigned users (Archived)</h5>
              <ul>
                {unAssignedUsers.map((each) => (
                  <li key={each}>{each}</li>
                ))}
              </ul>
            </div>
          )}

          {processing && <LinearProgress />}
        </form>
      </div>
      {showConfirmModal && (
        <ConfirmAction
          title='Confirm Chatbot Toggle'
          onConfirm={handleConfirmChatbotToggle}
          onCancel={handleCancelConfirm}
          onClose={handleCancelConfirm}
          confirm='Confirm'
          cancel='Cancel'
        >
          Are you sure you want to {chatbotEnabled ? 'enable' : 'disable'} the chatbot globally for all users?
        </ConfirmAction>
      )}
    </div>
  );
};

export default connect(null, { showError, showMessage })(BulkActions);
